import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Heading,
  TextInputField,
  FormField,
  Button,
  Pane,
  IconButton,
  SelectField,
  CrossIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Switch,
} from 'evergreen-ui';
import { Container, Row, Col, setConfiguration } from 'react-grid-system';

import { mutate } from 'swr';
import { useForm } from 'react-hook-form';

import { useQueryCategories } from '../../services/api/categoriesService';
import { useQueryBanks } from '../../services/api/banksService';
import { putEntry } from '../../services/api/entriesService';
import { getIntentColorByJournal, getNameByJournal } from '../../helpers/entriesHelper';
import styles from './EntryFormComponent.module.css';

import DividerComponent from '../../custom/DividerComponent';

import { toastNotification } from '../../shared/toastNotification';
import { formatWithLimitedDecimals } from '../../helpers/moneyFormatter';
import { useQueryVitaUserByIdentityId } from '../../services/api/vitaUserService';
import { isEntryReconciled, isTransferReconciled } from '../../helpers/reconciliationHelpers';

function EntryEditFormComponent({ isShown, setIsShown, journalEntry = {}, onSuccess }) {
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');

  /* Dialog settings  */

  const [isOpenAccordion, setIsOpenAccordion] = useState(true);

  /* API calls */

  const {
    data: vitaUser,
    isLoading: isLoadingVitaUser,
  } = useQueryVitaUserByIdentityId(userId, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: categories = [], isLoading: isLoadingCategories } = useQueryCategories(
    // company.data.id,
    companyId,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { data: banks = [], isLoading: isLoadingBanks } = useQueryBanks(
    // company.data.id,
    companyId,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  /* Form */

  const { register, handleSubmit, reset, getValues, setValue, watch } = useForm({
    // defaultValues: journalEntry,
  });

  const isEntryReconciledNow =
    journalEntry?.journalEntryType === 5
      ? isTransferReconciled(journalEntry)
      : isEntryReconciled(journalEntry);

  const isTransfer = journalEntry?.journalEntryType === 5;

  useEffect(() => {
    if (isShown) {
      const entry = {
        ...journalEntry,
        dateCompetencia: journalEntry.dateCompetencia.substr(0, 10),
        dateVencimento: journalEntry.dateVencimento.substr(0, 10),
      };
      reset(entry);

      setValue(
        'value',
        journalEntry.value.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        })
      );
      setValue(
        'fineValue',
        journalEntry.fineValue.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        })
      );
      setValue(
        'discountValue',
        journalEntry.discountValue.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        })
      );
      setValue(
        'interestValue',
        journalEntry.interestValue.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        })
      );
      setValue(
        'interestValuePercent',
        formatWithLimitedDecimals(
          (Number(getValues('interestValue').replace(/\./g, '').replace(',', '.')) /
            Number(getValues('value').replace(/\./g, '').replace(',', '.'))) *
            100
        )
      );
      mutate('banks');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown]);

  setConfiguration({
    containerWidths: '100%',
    gutterWidth: '12',
  });

  const handleInput = (event, type) => {
    let inputVal = event.target.value.replace(/\D/g, '');
    if (inputVal === '') {
      setValue(type, '');
      return;
    }
    inputVal = inputVal.padStart(3, '0');
    const wholeNum = inputVal.slice(0, -2);
    const decimal = inputVal.slice(-2);
    const formattedWholeNum = Number(wholeNum).toLocaleString();
    const formattedValue = `${formattedWholeNum},${decimal}`;

    setValue(type, formattedValue);
  };

  const buildFormHeader = () => {
    return (
      <Pane display="flex" flex={1} justifyContent="space-between" alignItems="center">
        <Button
          appearance="primary"
          intent={getIntentColorByJournal(journalEntry)}
          height={48}
          marginRight={16}
        >
          {getNameByJournal(journalEntry)}
        </Button>
        <Button
          appearance="minimal"
          intent="selected"
          height={28}
          marginRight={26}
          style={{
            background: '#BFFCC0',
            color: 'black',
          }}
          cursor="normal"
        >
          Modo Edição
        </Button>
        <IconButton appearance="minimal" icon={CrossIcon} onClick={onClose} />
      </Pane>
    );
  };

  const buildFormFooter = () => {
    return (
      <Pane>
        <Button form="entry-form" type="submit" width="fit-content">
          Salvar
        </Button>
      </Pane>
    );
  };

  const onSubmit = (entry) => {
    let statusResponse = false;
    let toastContent = '';

    let entryToUpdate = {
      id: entry.id,
      userUpdateId: !isLoadingVitaUser && vitaUser.data.id,
      userCreateId: entry.userCreateId,
      dateCompetencia: entry.dateCompetencia,
      dateVencimento: isEntryReconciledNow
        ? journalEntry.dateVencimento
        : entry.dateVencimento,
      paymentType: entry.paymentType,
      description: entry.description,
      statusPayment: entry.statusPayment,
      discountValue: isEntryReconciledNow
        ? journalEntry.discountValue
        : entry.discountValue
        ? Number(entry.discountValue.replace(/\./g, '').replace(',', '.'))
        : 0,
      fineValue: isEntryReconciledNow
        ? journalEntry.fineValue
        : !!entry.fineValue
        ? Number(entry.fineValue.replace(/\./g, '').replace(',', '.'))
        : 0,
      interestValue: isEntryReconciledNow
        ? journalEntry.interestValue
        : !!entry.interestValue
        ? Number(entry.interestValue.replace(/\./g, '').replace(',', '.'))
        : 0,
      value:
        isEntryReconciledNow
          ? journalEntry.value
          : typeof entry.value === typeof 5
          ? entry.value
          : Number(entry.value.replace(/\./g, '').replace(',', '.')),
      categoryId: entry.categoryId,
      bankAccountId: isEntryReconciledNow ? journalEntry.bankAccountId : entry.bankAccountId,
      bankAccountIdDestination: entry.bankAccountIdDestination,
    };

    putEntry(entryToUpdate)
      .then((res) => {
        mutate('entriesFilteredByDate');
        mutate('entriesReconciliation');
        mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
        mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
        statusResponse = 'success';
        toastContent = 'Edição salva.';
        // Call onSuccess callback before closing (used by bulk edit flow)
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      })
      .catch((err) => {
        statusResponse = 'warning';
        toastContent = `Erro. Verifique os campos novamente.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        mutate('banks');
      });
  };

  const onClose = () => {
    reset();
    setIsOpenAccordion(false);
    setIsShown(false);
  };

  const openAdvancedOptions = () => {
    setIsOpenAccordion((prevState) => !prevState);
  };

  useEffect(() => {
    if (!isShown || !journalEntry || !banks || !banks.data) return;

    // Allow editing all fields for all entries

    const bank = banks.data.find((bank) => bank.id === journalEntry.bankAccountId);
    if (!bank) {
      // Allow editing even if reconciled
      return;
    }

    // Allow editing even if date is before reconciliation date
  }, [isShown, journalEntry, banks]);

  const watchedBankAccountId = watch('bankAccountId');
  useEffect(() => {
    if (!banks || !isShown) return;

    const bank = banks.data.find((bank) => bank.id === watchedBankAccountId);
    if (!bank) {
      return;
    }

    // Allow any date regardless of reconciliation
  }, [watchedBankAccountId, banks, isShown]);

  return (
    <Dialog
      isShown={isShown}
      title=""
      onCloseComplete={onClose}
      confirmLabel="Custom Label"
      hasFooter={true}
      header={buildFormHeader}
      footer={buildFormFooter}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEscapePress={true}
      width={800}
      backgroundColor="#f8f8f8"
    >
      <div className={styles.dialogWrapper}>
        <form id="entry-form" onSubmit={handleSubmit(onSubmit)}>
          <FormField className={styles.formArea}>
            <Container
              fluid={true}
              style={{
                width: '100%',
                margin: 'none',
                padding: 'none',
              }}
            >
              {isTransfer ? (
                <>
                  <Row>
                    <Col sm={12} md={6} lg={6}>
                      <SelectField
                        {...register('bankAccountId')}
                        label="Conta de Saída"
                        onChange={(e) => setValue('bankAccountId', e.target.value)}
                        disabled={isEntryReconciledNow}
                      >
                        {!isLoadingBanks &&
                          banks.data
                            .sort((a, b) => {
                              const nameA = a.name;
                              const nameB = b.name;
                              if (nameA < nameB) return -1;
                              if (nameA > nameB) return 1;
                              return 0;
                            })
                            .map((bank) => (
                              <option key={bank.id} value={bank.id}>
                                {bank.name}
                              </option>
                            ))}
                      </SelectField>
                    </Col>
                    <Col sm={12} md={6} lg={6}>
                      <SelectField
                        {...register('bankAccountIdDestination')}
                        label="Conta de Entrada"
                        onChange={(e) => setValue('bankAccountIdDestination', e.target.value)}
                        disabled={isEntryReconciledNow}
                      >
                        {!isLoadingBanks &&
                          banks.data
                            .sort((a, b) => {
                              const nameA = a.name;
                              const nameB = b.name;
                              if (nameA < nameB) return -1;
                              if (nameA > nameB) return 1;
                              return 0;
                            })
                            .map((bank) => (
                              <option key={bank.id} value={bank.id}>
                                {bank.name}
                              </option>
                            ))}
                      </SelectField>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} md={6} lg={6}>
                      <TextInputField
                        {...register('dateCompetencia')}
                        label="Data da Transferência"
                        id="dateCompetencia"
                        name="dateCompetencia"
                        type="date"
                      />
                    </Col>
                    <Col sm={12} md={6} lg={6}>
                      <TextInputField
                        {...register('description')}
                        label="Descrição"
                        id="description"
                        name="description"
                        placeholder=""
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={10} sm={11} md={11} lg={11}>
                      <TextInputField
                        {...register('value', {
                          required: true,
                        })}
                        defaultValue="0,00"
                        label="Valor (R$)"
                        type="text"
                        id="value"
                        name="value"
                        placeholder=""
                        onChange={(e) => handleInput(e, 'value')}
                        disabled={isEntryReconciledNow}
                      />
                    </Col>
                    <Col xs={2} sm={1} md={1} lg={1}>
                      <Heading size={400} marginBottom={16}>
                        Pago
                      </Heading>
                      <Switch
                        {...register('statusPayment')}
                        checked={watch('statusPayment')}
                        onChange={(e) => {
                          setValue('statusPayment', e.target.checked);
                        }}
                      />
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row>
                    <Col sm={12} md={6} lg={4}>
                      <TextInputField
                        {...register('dateCompetencia')}
                        label="Data de competência"
                        id="dateCompetencia"
                        name="dateCompetencia"
                        type="date"
                      />
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                      <TextInputField
                        {...register('dateVencimento')}
                        label="Data de vencimento"
                        id="dateVencimento"
                        name="dateVencimento"
                        type="date"
                        disabled={isEntryReconciledNow}
                      />
                    </Col>

                    <Col sm={12} md={12} lg={4}>
                      <SelectField
                        {...register('bankAccountId')}
                        label="Conta"
                        onChange={(e) => setValue('bankAccountId', e.target.value)}
                        disabled={isEntryReconciledNow}
                      >
                        {!isLoadingBanks &&
                          banks.data
                            .sort((a, b) => {
                              const nameA = a.name;
                              const nameB = b.name;

                              if (nameA < nameB) {
                                return -1;
                              }

                              if (nameA > nameB) {
                                return 1;
                              }

                              return 0;
                            })
                            .map((bank) => {
                              return (
                                <option key={bank.id} value={bank.id}>
                                  {bank.name}
                                </option>
                              );
                            })}
                      </SelectField>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} md={6} lg={6}>
                      <SelectField
                        {...register('categoryId')}
                        label="Categoria"
                        onChange={(e) => setValue('categoryId', e.target.value)}
                      >
                        {!isLoadingCategories &&
                          categories &&
                          categories.data &&
                          Array.isArray(categories.data) &&
                          categories.data
                            .filter((category) => {
                              // Mantém a categoria já selecionada visível mesmo se ela tiver
                              // sido desabilitada depois, senão o campo apareceria vazio.
                              return (
                                category.isInput === watch('isInput') &&
                                (category.enabled || category.id === watch('categoryId'))
                              );
                            })
                            .sort((a, b) => {
                              const nameA = a.name;
                              const nameB = b.name;

                              if (nameA < nameB) {
                                return -1;
                              }

                              if (nameA > nameB) {
                                return 1;
                              }

                              return 0;
                            })
                            .map((item) => {
                              return (
                                <option key={item.id} value={item.id}>
                                  {item.name}
                                </option>
                              );
                            })}
                      </SelectField>
                    </Col>
                    <Col sm={12} md={6} lg={6}>
                      <TextInputField
                        {...register('description')}
                        label="Descrição"
                        id="description"
                        name="description"
                        placeholder=""
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={10} sm={11} md={11} lg={11}>
                      <TextInputField
                        {...register('value', {
                          required: true,
                          // min: 0,
                          // valueAsNumber: true,
                        })}
                        defaultValue="0,00"
                        label="Valor (R$)"
                        type="text"
                        // min='0,00'
                        id="value"
                        name="value"
                        placeholder=""
                        // step='0.01'
                        onChange={(e) => handleInput(e, 'value')}
                        disabled={isEntryReconciledNow}
                      />
                    </Col>
                    <Col xs={2} sm={1} md={1} lg={1}>
                      <Heading size={400} marginBottom={16}>
                        Pago
                      </Heading>
                      <Switch
                        {...register('statusPayment')}
                        checked={watch('statusPayment')}
                        onChange={(e) => {
                          setValue('statusPayment', e.target.checked);
                        }}
                      />
                    </Col>
                  </Row>
                  <Pane display="flex" flexWrap="nowrap">
                    <DividerComponent padding="0px" color="#e8e8e8" />
                    {isOpenAccordion ? (
                      <IconButton icon={ChevronUpIcon} type="button" onClick={openAdvancedOptions} />
                    ) : (
                      <IconButton icon={ChevronDownIcon} type="button" onClick={openAdvancedOptions} />
                    )}
                  </Pane>
                  <div
                    style={{
                      display: `${isOpenAccordion ? 'initial' : 'none'}`,
                    }}
                  >
                    <Row>
                      <Col sm={12} md={6} lg={6}>
                        <TextInputField
                          {...register('discountValue')}
                          label="Desconto"
                          type="text"
                          defaultValue="0,00"
                          id="discountValue"
                          name="discountValue"
                          placeholder=""
                          disabled={!watch('value') || isEntryReconciledNow}
                          onChange={(e) => handleInput(e, 'discountValue')}
                        />
                      </Col>
                      <Col sm={12} md={6} lg={6}>
                        <TextInputField
                          {...register('fineValue')}
                          label="Multa"
                          type="text"
                          defaultValue="0,00"
                          id="fineValue"
                          name="fineValue"
                          placeholder=""
                          disabled={!watch('value') || isEntryReconciledNow}
                          onChange={(e) => handleInput(e, 'fineValue')}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={9} sm={9} md={8}>
                        <TextInputField
                          {...register('interestValue')}
                          label="Juros"
                          type="text"
                          defaultValue="0,00"
                          id="interestValue"
                          name="interestValue"
                          placeholder=""
                          disabled={!watch('value') || isEntryReconciledNow}
                          onChange={(val) => {
                            handleInput(val, 'interestValue');
                            let v =
                              (Number(val.target.value.replace(/\./g, '').replace(',', '.')) /
                                Number(watch('value').replace(/\./g, '').replace(',', '.'))) *
                              100;
                            setValue('interestValuePercent', formatWithLimitedDecimals(v));
                          }}
                        />
                      </Col>
                      <Col xs={3} sm={3} md={4}>
                        <TextInputField
                          {...register('interestValuePercent')}
                          label="%"
                          type="number"
                          min="0"
                          id="interestValuePercent"
                          name="interestValuePercent"
                          defaultValue={0}
                          placeholder=""
                          step="0.01"
                          disabled={!watch('value') || isEntryReconciledNow}
                          onChange={(val) => {
                            let v =
                              (val.target.value *
                                Number(watch('value').replace(/\./g, '').replace(',', '.'))) /
                              100;
                            setValue('interestValue', formatWithLimitedDecimals(v));
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                </>
              )}
            </Container>
          </FormField>
        </form>
      </div>

      {/* <div> Rodapé </div> */}
    </Dialog>
  );
}

export default EntryEditFormComponent;
